import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, ShieldCheck, XCircle, AlertTriangle, Clock,
  Loader2, Inbox, RotateCw, Hash, Send, Eye, X as XIcon, Mail, Settings,
} from "lucide-react";
import AlertRecipientsModal from "@/components/admin/AlertRecipientsModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;


function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return s; }
}


function OutcomeBadge({ outcome }) {
  if (outcome === "approved") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#3a8f5a] bg-[#E6F5EC] text-[#1e5a36]"
        data-testid={`outcome-badge-approved`}
      >
        <ShieldCheck className="w-3 h-3" /> Approved
      </span>
    );
  }
  if (outcome === "denied") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#C05746] bg-[#FBEAE5] text-[#8A2E1A]"
        data-testid={`outcome-badge-denied`}
      >
        <XCircle className="w-3 h-3" /> Denied
      </span>
    );
  }
  return null;
}


function SyncPill({ row }) {
  if (row.avida_synced) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-[#3a8f5a] bg-[#E6F5EC] text-[#1e5a36]"
        data-testid={`sync-pill-synced-${row.draft_id}`}
        title={row.avida_synced_at ? `Synced ${fmtDate(row.avida_synced_at)}` : ""}
      >
        <ShieldCheck className="w-3 h-3" /> Synced to Avida
      </span>
    );
  }
  if (row.avida_sync_failed) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-[#C05746] bg-[#FBEAE5] text-[#8A2E1A]"
        data-testid={`sync-pill-failed-${row.draft_id}`}
        title={row.avida_last_error || ""}
      >
        <AlertTriangle className="w-3 h-3" /> Sync failed
        {row.avida_attempt_count ? ` (${row.avida_attempt_count}/3)` : ""}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-[#B5C8DD] bg-[#F1F5F9] text-[#3A5275]"
      data-testid={`sync-pill-pending-${row.draft_id}`}
      title="Webhook hasn't completed yet, or preview short-circuit (no AVIDA_CARE_URL)"
    >
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}


export default function PaOutcomesInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");
  const [resending, setResending] = useState({}); // { draft_id: true }
  const [digestBusy, setDigestBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (outcomeFilter !== "all") params.set("outcome", outcomeFilter);
      if (syncFilter !== "all") params.set("sync", syncFilter);
      const r = await fetch(`${API}/intake/drafts/pa-outcomes?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.outcomes || []);
    } catch (e) {
      toast.error(`Failed to load PA outcomes: ${e.message || "unknown"}`);
    } finally { setLoading(false); }
  }, [outcomeFilter, syncFilter]);

  useEffect(() => { load(); }, [load]);

  const resend = async (draftId) => {
    setResending((p) => ({ ...p, [draftId]: true }));
    try {
      const r = await fetch(
        `${API}/intake/drafts/${encodeURIComponent(draftId)}/pa-outcome/resend`,
        { method: "POST" },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        let detail = ""; try { detail = JSON.parse(text)?.detail || ""; }
        catch { detail = text; }
        throw new Error(detail || `HTTP ${r.status}`);
      }
      toast.success("Re-queued — Avida sync will retry now");
      // Refresh to surface the reset state immediately
      load();
    } catch (e) {
      toast.error(`Resend failed: ${e.message}`);
    } finally {
      setResending((p) => {
        const next = { ...p }; delete next[draftId]; return next;
      });
    }
  };

  const previewDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await fetch(`${API}/admin/digest/preview`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDigestPreview(await r.json());
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally { setDigestBusy(false); }
  };

  const sendDigestNow = async () => {
    if (!window.confirm(
      "Send today's digest to Slack now? (If no SLACK_DIGEST_WEBHOOK_URL is configured, this short-circuits and nothing is posted.)"
    )) return;
    setDigestBusy(true);
    try {
      const r = await fetch(`${API}/admin/digest/run`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (j.skipped === "no_slack_webhook_configured") {
        toast.info("No Slack webhook configured — digest built but not posted");
      } else if (j.ok) {
        toast.success("Digest posted to Slack");
      } else {
        toast.error(`Digest delivery failed: ${j.delivery?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally { setDigestBusy(false); }
  };

  const sendTestEmail = async () => {
    const recipientsRaw = window.prompt(
      "Send a test alert email to which address(es)?\n\n" +
      "Comma-separated. Leave blank to use the default fallback recipients " +
      "from EMAIL_ALERT_FALLBACK_RECIPIENTS.",
      "",
    );
    if (recipientsRaw === null) return; // cancelled
    const recipients = recipientsRaw
      .split(",").map((s) => s.trim()).filter(Boolean);
    setEmailBusy(true);
    try {
      const r = await fetch(`${API}/admin/email/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipients }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        let detail = ""; try { detail = JSON.parse(t)?.detail || ""; }
        catch { detail = t; }
        throw new Error(detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      if (j.delivery?.skipped === "no_gmail_password_configured") {
        toast.info(
          "No GMAIL_APP_PASSWORD configured — email built but not delivered. " +
          "Add it to backend/.env to go live.",
        );
      } else if (j.delivery?.skipped === "no_recipients_resolved") {
        toast.error(
          "No recipients resolved — set per-agency alert_emails on " +
          "Agency Branding, or set EMAIL_ALERT_FALLBACK_RECIPIENTS env.",
        );
      } else if (j.ok) {
        toast.success(
          `Test email delivered to ${j.recipients.join(", ")} ` +
          `(via ${j.recipient_source})`,
        );
      } else {
        const err = j.delivery?.error || "unknown";
        toast.error(`Email delivery failed: ${err}`);
      }
    } catch (e) {
      toast.error(`Test email failed: ${e.message}`);
    } finally { setEmailBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, approved: 0, denied: 0,
                synced: 0, failed: 0, pending: 0 };
    for (const r of rows) {
      if (r.outcome === "approved") c.approved += 1;
      if (r.outcome === "denied") c.denied += 1;
      if (r.avida_synced) c.synced += 1;
      else if (r.avida_sync_failed) c.failed += 1;
      else c.pending += 1;
    }
    return c;
  }, [rows]);

  return (
    <main className="bg-paper min-h-screen pb-16" data-testid="pa-outcomes-inbox">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <Link
          to="/drafts/pending"
          className="inline-flex items-center gap-1.5 text-[12px] text-[#3A5275] hover:text-[#0A3D79]"
          data-testid="back-to-pending"
        >
          <ArrowLeft className="w-4 h-4" /> Back to pending drafts
        </Link>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">
              Admin · Avida sync
            </div>
            <h1 className="font-display text-3xl font-semibold text-[#0B1E3A]">
              PA outcomes inbox
            </h1>
            <p className="text-[13px] text-[#3A5275] mt-1 max-w-2xl">
              Coordinator view of every recorded ForwardHealth PA outcome and the
              status of its outbound Avida sync. Re-fire any failed delivery with
              one click — no need to re-enter the approval data.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={previewDigest}
              disabled={digestBusy}
              className="btn-ghost"
              data-testid="digest-preview-btn"
              title="See what today's 8am Slack digest will look like — builds only, does not post."
            >
              <Eye className="w-4 h-4" /> Preview digest
            </button>
            <button
              type="button"
              onClick={sendDigestNow}
              disabled={digestBusy}
              className="btn-ghost"
              data-testid="digest-send-now-btn"
              title="Post today's digest to Slack now (uses SLACK_DIGEST_WEBHOOK_URL). No-op in preview when unset."
            >
              {digestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to Slack now
            </button>
            <button
              type="button"
              onClick={sendTestEmail}
              disabled={emailBusy}
              className="btn-ghost"
              data-testid="email-test-btn"
              title="Send a sample alert email to verify SMTP wiring + sender + per-agency recipient routing. No-op in preview when GMAIL_APP_PASSWORD is unset."
            >
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send test email
            </button>
            <button
              type="button"
              onClick={() => setRecipientsModalOpen(true)}
              className="btn-ghost"
              data-testid="email-recipients-btn"
              title="Edit per-agency alert recipients"
            >
              <Settings className="w-4 h-4" />
              Recipients
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="btn-ghost"
              data-testid="reload-pa-outcomes"
              title="Reload"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Reload
            </button>
          </div>
        </div>

        {/* Counters */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="rounded-xl border border-[#D6E3EF] bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">Total</div>
            <div className="font-display text-2xl font-semibold text-[#0B1E3A]" data-testid="counter-total">
              {counts.total}
            </div>
          </div>
          <div className="rounded-xl border border-[#3a8f5a] bg-[#F4FAF6] px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#1e5a36] font-semibold">Approved</div>
            <div className="font-display text-2xl font-semibold text-[#1e5a36]" data-testid="counter-approved">
              {counts.approved}
            </div>
          </div>
          <div className="rounded-xl border border-[#C05746] bg-[#FDF5F3] px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A2E1A] font-semibold">Denied</div>
            <div className="font-display text-2xl font-semibold text-[#8A2E1A]" data-testid="counter-denied">
              {counts.denied}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 rounded-full bg-[#F1F5F9] p-0.5" data-testid="outcome-filter">
            {[
              { key: "all", label: "All outcomes" },
              { key: "approved", label: "Approved" },
              { key: "denied", label: "Denied" },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setOutcomeFilter(f.key)}
                className={[
                  "px-3 py-1 text-[12px] font-semibold rounded-full transition-colors",
                  outcomeFilter === f.key
                    ? "bg-white text-[#0A3D79] shadow-sm"
                    : "text-[#3A5275] hover:text-[#0A3D79]",
                ].join(" ")}
                data-testid={`outcome-filter-${f.key}`}
                aria-pressed={outcomeFilter === f.key}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full bg-[#F1F5F9] p-0.5" data-testid="sync-filter">
            {[
              { key: "all", label: "All sync" },
              { key: "synced", label: "Synced" },
              { key: "failed", label: "Failed" },
              { key: "pending", label: "Pending" },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setSyncFilter(f.key)}
                className={[
                  "px-3 py-1 text-[12px] font-semibold rounded-full transition-colors",
                  syncFilter === f.key
                    ? "bg-white text-[#0A3D79] shadow-sm"
                    : "text-[#3A5275] hover:text-[#0A3D79]",
                ].join(" ")}
                data-testid={`sync-filter-${f.key}`}
                aria-pressed={syncFilter === f.key}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-5 rounded-2xl border border-[#D6E3EF] bg-white overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center text-[13px] text-[#3A5275]" data-testid="loading-state">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-12 text-center" data-testid="empty-state">
              <Inbox className="w-8 h-8 text-[#B5C8DD] mx-auto mb-2" />
              <div className="text-[13px] text-[#3A5275]">
                No PA outcomes match the current filter.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="outcomes-table">
                <thead>
                  <tr className="bg-[#F7FAFD] border-b border-[#D6E3EF]">
                    <th className="text-left px-3 py-2 font-semibold text-[#3A5275]">Client</th>
                    <th className="text-left px-3 py-2 font-semibold text-[#3A5275]">Outcome</th>
                    <th className="text-left px-3 py-2 font-semibold text-[#3A5275]">Detail</th>
                    <th className="text-left px-3 py-2 font-semibold text-[#3A5275]">Recorded</th>
                    <th className="text-left px-3 py-2 font-semibold text-[#3A5275]">Avida sync</th>
                    <th className="text-right px-3 py-2 font-semibold text-[#3A5275]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.draft_id}
                      className="border-b border-[#EEF2F7] last:border-b-0 hover:bg-[#F7FAFD]"
                      data-testid={`outcome-row-${r.draft_id}`}
                    >
                      <td className="px-3 py-3">
                        <div className="font-semibold text-[#0B1E3A]">{r.client_name || "—"}</div>
                        <div className="font-mono text-[10.5px] text-[#3A5275] mt-0.5 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {r.draft_id}
                        </div>
                        {r.assigned_rn && (
                          <div className="text-[10.5px] text-[#3A5275] mt-0.5">{r.assigned_rn}</div>
                        )}
                      </td>
                      <td className="px-3 py-3"><OutcomeBadge outcome={r.outcome} /></td>
                      <td className="px-3 py-3">
                        {r.outcome === "approved" ? (
                          <div className="space-y-0.5">
                            {r.pa_number && (
                              <div className="text-[#0B1E3A]">PA #{r.pa_number}</div>
                            )}
                            {r.approved_units_per_week && (
                              <div className="text-[#3A5275]">{r.approved_units_per_week} units/week</div>
                            )}
                          </div>
                        ) : r.outcome === "denied" ? (
                          <div className="text-[#8A2E1A] max-w-xs">
                            <span className="line-clamp-2" title={r.denial_reason}>
                              {r.denial_reason || "Reason not provided"}
                            </span>
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-[#3A5275]">
                        <div>{fmtDate(r.recorded_at)}</div>
                        {r.recorded_by && (
                          <div className="text-[10.5px] mt-0.5">by {r.recorded_by}</div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <SyncPill row={r} />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {r.job_id && (
                            <Link
                              to={`/download/${r.job_id}`}
                              className="text-[11.5px] font-semibold text-[#0A3D79] hover:underline"
                              data-testid={`view-downloads-${r.draft_id}`}
                            >
                              View files
                            </Link>
                          )}
                          {r.avida_sync_failed && (
                            <button
                              type="button"
                              onClick={() => resend(r.draft_id)}
                              disabled={!!resending[r.draft_id]}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#0A3D79] text-[#0A3D79] bg-white hover:bg-[#EEF5FC] text-[11.5px] font-semibold disabled:opacity-60"
                              data-testid={`resend-btn-${r.draft_id}`}
                              title="Re-fire the Avida outbound webhook for this PA outcome"
                            >
                              {resending[r.draft_id] ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> Re-sending…</>
                              ) : (
                                <><RotateCw className="w-3 h-3" /> Re-send to Avida</>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {digestPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="digest-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setDigestPreview(null); }}
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-[#D6E3EF] max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#D6E3EF]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">
                  Digest preview · last 24h
                </div>
                <h2 className="font-display text-xl font-semibold text-[#0B1E3A]">
                  What would post to Slack
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setDigestPreview(null)}
                className="p-1.5 rounded-md text-[#3A5275] hover:bg-[#F1F5F9]"
                data-testid="digest-preview-close"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div
                className="rounded-lg border border-[#B7CADA] bg-[#EEF5FC] px-3 py-2 text-[12px] text-[#0B1E3A] font-mono whitespace-pre-wrap"
                data-testid="digest-preview-fallback"
              >
                {digestPreview.fallback_text}
              </div>
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">
                  Rendered blocks
                </div>
                <pre
                  className="rounded-lg bg-[#0B1E3A] text-[#E6F2FF] text-[11px] leading-relaxed p-3 overflow-x-auto font-mono max-h-80"
                  data-testid="digest-preview-blocks"
                >
                  {JSON.stringify(digestPreview.blocks, null, 2)}
                </pre>
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="digest-preview-counters">
                {Object.entries({
                  Total: digestPreview.digest.total,
                  Approved: digestPreview.digest.approved,
                  Denied: digestPreview.digest.denied,
                  "Synced 1st-try": digestPreview.digest.synced_first_try,
                  "Auto-recovered": digestPreview.digest.auto_recovered,
                  Failed: digestPreview.digest.failed_terminal,
                }).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-[#D6E3EF] bg-white px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">
                      {k}
                    </div>
                    <div className="font-display text-xl font-semibold text-[#0B1E3A]">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-[#D6E3EF] bg-[#F7FAFD] rounded-b-2xl">
              <button
                type="button"
                onClick={() => setDigestPreview(null)}
                className="btn-ghost"
                data-testid="digest-preview-cancel"
              >
                Close
              </button>
              <button
                type="button"
                onClick={async () => { await sendDigestNow(); setDigestPreview(null); }}
                disabled={digestBusy}
                className="btn-primary"
                data-testid="digest-preview-send"
              >
                {digestBusy
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  : <><Send className="w-4 h-4" /> Send this to Slack</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertRecipientsModal
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
      />
    </main>
  );
}
